/**
 * Video-time helpers: seconds into the FILE, not the game clock.
 *
 * Distinct from lib/viz/frameTime on purpose — that module maps tracking
 * frames to video time through the sync offsets; this one only formats and
 * parses raw video timestamps (the domain the halftime tick marks and the
 * sync modal store). Minutes run unbounded ("71:03", not "1:11:03") because
 * that is the grammar the match clock already uses (formatGameTime).
 */

/** 3267 -> "54:27". Fractional seconds are floored — a tick mark is a place
 *  in the video, and sub-second precision is noise at this grain. */
export function formatVideoSeconds(seconds: number): string {
  const whole = Math.floor(seconds)
  const m = Math.floor(whole / 60)
  const s = whole % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

/**
 * "54:27" | "1:05:19" | "3267" | "3267.5" -> seconds.
 * Blank means CLEAR and returns null. Anything unparseable returns NaN so
 * the caller can refuse it — null and NaN are different answers here.
 */
export function parseVideoTime(input: string): number | null {
  const t = input.trim()
  if (!t) return null
  if (t.includes(':')) {
    const parts = t.split(':')
    if (parts.length > 3 || parts.some((p) => p === '' || !/^\d+$/.test(p))) return NaN
    const nums = parts.map((p) => parseInt(p, 10))
    // mm:ss or h:mm:ss; the seconds (and minutes, in the 3-part form) must
    // be a real clock position, not "5:99".
    if (nums.length === 2) {
      const [m, s] = nums as [number, number]
      return s < 60 ? m * 60 + s : NaN
    }
    const [h, m, s] = nums as [number, number, number]
    return m < 60 && s < 60 ? h * 3600 + m * 60 + s : NaN
  }
  const v = Number(t)
  return Number.isFinite(v) && v >= 0 ? v : NaN
}
